// ListingEditorModal.jsx
import React, { useState } from 'react';
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Spinner,
  Alert
} from 'react-bootstrap';
import {
  getFirestore,
  collection,
  doc,
  getDocs,
  query,
  updateDoc,
  where,
  writeBatch,
  serverTimestamp
} from 'firebase/firestore';
import {
  getStorage,
  ref as sRef,
  uploadBytes,
  getDownloadURL,
  deleteObject,
  listAll
} from 'firebase/storage';
import { getAuth } from 'firebase/auth';
import './ListingEditorModal.css';

export default function ListingEditorModal({ show, listing, onClose }) {
  const db = getFirestore();
  const storage = getStorage();
  const auth = getAuth();

  // form fields
  const [title, setTitle]             = useState(listing.title);
  const [description, setDescription] = useState(listing.description);
  const [city, setCity]               = useState(listing.city);
  const [zipCode, setZipCode]         = useState(listing.zipCode);
  const [stateField, setStateField]   = useState(listing.state);
  const [price, setPrice]             = useState(listing.price);

  // images
  const [urls, setUrls]   = useState([
    listing.image1||'', listing.image2||'', listing.image3||''
  ]);

  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const isUpdateDisabled =
    saving ||
    (title === listing.title &&
      description === listing.description &&
      city === listing.city &&
      zipCode === listing.zipCode &&
      stateField === listing.state &&
      price === listing.price
    );

  // upload new image → Firestore
  const handleFileChange = async (e, idx) => {
    const file = e.target.files[0];
    if (!file) return;
    setSaving(true);
    setError('');
    try {
      const userId = auth.currentUser.uid;
      const refPath = `forSaleImages/${userId}/${listing.id}_${idx+1}.jpg`;
      const storageRef = sRef(storage, refPath);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);

      await updateDoc(doc(db, 'forsale', listing.id), {
        [`image${idx+1}`]: url,
        status: 'unverified'
      });

      setUrls(u => { const next = [...u]; next[idx] = url; return next; });
    } catch {
      setError('Image upload failed');
    }
    setSaving(false);
  };

  // delete one image
  const handleImageDelete = async idx => {
    setSaving(true);
    setError('');
    try {
      const url = urls[idx];
      if (url) {
        const refToDel = sRef(storage, url);
        await deleteObject(refToDel);
        await updateDoc(doc(db, 'forsale', listing.id), {
          [`image${idx+1}`]: ''
        });
      }
      setUrls(u => { const n = [...u]; n[idx] = ''; return n; });
    } catch {
      setError('Failed to delete image');
    }
    setSaving(false);
  };

  // update text & price fields
  const handleUpdate = async () => {
    setSaving(true);
    setError('');
    try {
      await updateDoc(doc(db, 'forsale', listing.id), {
        title,
        description,
        city,
        zipCode,
        state: stateField,
        price,
        status: 'unverified',
        timestamp: serverTimestamp()
      });
      onClose();
    } catch {
      setError('Update failed');
    }
    setSaving(false);
  };

  // mark listing deleted
  const handleDelete = async () => {
    setSaving(true);
    setError('');
    try {
      await updateDoc(doc(db, 'forsale', listing.id), {
        status: 'deleted',
        deletedAt: serverTimestamp(),
        timeUpdated: serverTimestamp(),
      });

      // Delete listing-specific conversation records with the modular Firestore API.
      const inboxQ = await getDocs(query(
        collection(db, 'globalinbox'),
        where('forsaleId', '==', listing.id)
      ));
      const batch = writeBatch(db);
      inboxQ.forEach((conversation) => batch.delete(conversation.ref));
      await batch.commit();

      // delete all storage images
      const listRef = sRef(storage, `forSaleImages/${listing.receiverId}`);
      const listResult = await listAll(listRef);
      await Promise.all(
        listResult.items
          .filter(i => i.name.startsWith(listing.id))
          .map(i => deleteObject(i))
      );

      // Keep the soft-deleted document for moderation and cross-platform history.
      onClose();
    } catch {
      setError('Delete failed');
    }
    setSaving(false);
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      size="lg"
      centered
      scrollable
      backdrop="static"
      keyboard={false}
      dialogClassName="manage-listing-dialog"
      contentClassName="manage-listing-content"
      backdropClassName="manage-listing-backdrop"
    >
      <Modal.Header className="manage-listing-header" closeButton closeVariant="white">
        <Modal.Title>Edit Listing</Modal.Title>
      </Modal.Header>

      <Modal.Body className="manage-listing-body">
        {error && <Alert variant="danger">{error}</Alert>}

        <Row className="manage-listing-image-row mb-3 gx-2">
          {[0,1,2].map(idx => (
            <Col key={idx} xs={4} className="position-relative">
              {urls[idx]
                ? <img src={urls[idx]} alt="" className="manage-listing-img-preview" />
                : <div className="manage-listing-img-placeholder">+</div>
              }
              <input
                type="file"
                accept="image/*"
                className="manage-listing-file-input"
                onChange={e => handleFileChange(e, idx)}
                disabled={saving}
              />
              {urls[idx] && (
                <Button
                  variant="light"
                  size="sm"
                  className="manage-listing-remove-btn"
                  onClick={() => handleImageDelete(idx)}
                  disabled={saving}
                >×</Button>
              )}
            </Col>
          ))}
        </Row>

        <Form className="manage-listing-form">
          <Form.Group className="manage-listing-form-group mb-2">
            <Form.Label>Title</Form.Label>
            <Form.Control
              value={title}
              onChange={e => setTitle(e.target.value)}
            />
          </Form.Group>

          <Form.Group className="manage-listing-form-group mb-2">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={description}
              onChange={e => setDescription(e.target.value)}
            />
          </Form.Group>

          <Row className="manage-listing-form-row gx-2">
            <Col>
              <Form.Group className="manage-listing-form-group mb-2">
                <Form.Label>City</Form.Label>
                <Form.Control
                  value={city}
                  onChange={e => setCity(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="manage-listing-form-group mb-2">
                <Form.Label>State</Form.Label>
                <Form.Control
                  value={stateField}
                  onChange={e => setStateField(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="manage-listing-form-group mb-2">
                <Form.Label>Zip</Form.Label>
                <Form.Control
                  value={zipCode}
                  onChange={e => setZipCode(e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="manage-listing-form-group mb-3">
            <Form.Label>Price</Form.Label>
            <Form.Control
              value={price}
              onChange={e => setPrice(e.target.value)}
            />
          </Form.Group>
        </Form>
      </Modal.Body>

      <Modal.Footer className="manage-listing-footer">
        <Button
          variant="outline-danger"
          onClick={handleDelete}
          disabled={saving}
        >
          {saving ? <Spinner size="sm" /> : 'Delete'}
        </Button>
        <Button
          variant="primary"
          onClick={handleUpdate}
          disabled={isUpdateDisabled}
        >
          {saving ? <Spinner size="sm" /> : 'Update'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
